import { useCallback, useEffect, useState } from "react";
import { KeyRound, Plus, Trash2, Copy, Webhook, BarChart3, BookOpen, X } from "lucide-react";
import api, { fmtDateTime, fmtErr } from "../../lib/api";
import { toast } from "sonner";

const EVENTS = ["shipment.created", "shipment.status", "shipment.delivered", "shipment.ndr", "shipment.rto", "shipment.cancelled", "return.created", "cod.settled", "wallet.adjusted"];
const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-shipany-blue focus:outline-none";
const API_BASE = process.env.REACT_APP_BACKEND_URL;

const DOCS = [
  ["POST", "/api/v1/rates", "Calculate rates across all couriers"],
  ["POST", "/api/v1/shipments", "Create + book shipment (returns AWB)"],
  ["GET", "/api/v1/track/{awb}", "Track a shipment"],
  ["POST", "/api/v1/shipments/{id}/cancel", "Cancel before pickup (auto refund)"],
  ["GET", "/api/v1/wallet", "Wallet balance"],
  ["GET", "/api/v1/invoices", "List invoices"],
];

export default function Developer() {
  const [tab, setTab] = useState("keys");
  const [keys, setKeys] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [usage, setUsage] = useState(null);
  const [open, setOpen] = useState(false);
  const [whOpen, setWhOpen] = useState(false);
  const [form, setForm] = useState({ name: "", mode: "sandbox" });
  const [whForm, setWhForm] = useState({ url: "", events: ["shipment.status"] });
  const [newKey, setNewKey] = useState(null);

  const load = useCallback(() => {
    api.get("/developer/keys").then(({ data }) => setKeys(data)).catch(() => {});
    api.get("/developer/webhooks").then(({ data }) => setWebhooks(data)).catch(() => {});
    api.get("/developer/webhooks/deliveries").then(({ data }) => setDeliveries(data)).catch(() => {});
    api.get("/developer/usage").then(({ data }) => setUsage(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const createKey = async () => {
    try {
      const { data } = await api.post("/developer/keys", form);
      setNewKey(data.api_key);
      toast.success("API key created — copy it now, it won't be shown again");
      setOpen(false);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const createWebhook = async () => {
    try {
      await api.post("/developer/webhooks", whForm);
      toast.success("Webhook endpoint registered");
      setWhOpen(false);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };

  return (
    <div data-testid="developer-page">
      <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">Developer Portal</h1>
      <p className="text-sm text-slate-500">SHIPANY API keys, outbound webhooks, usage and docs.</p>

      <div className="mt-5 flex flex-wrap gap-2" data-testid="developer-tabs">
        {[["keys", "API Keys", KeyRound], ["webhooks", "Webhooks", Webhook], ["usage", "Usage & Logs", BarChart3], ["docs", "API Docs", BookOpen]].map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`dev-tab-${k}`}
            className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold ${tab === k ? "bg-shipany-navy text-white" : "border border-slate-200 bg-white text-slate-500"}`}>
            <Icon className="h-4 w-4" />{l}
          </button>
        ))}
      </div>

      {tab === "keys" && (
        <div className="mt-5 space-y-4">
          <button onClick={() => setOpen(true)} data-testid="create-key-btn" className="flex items-center gap-2 rounded-xl bg-shipany-blue px-5 py-2.5 text-sm font-bold text-white hover:bg-[#0648B3] glow-blue"><Plus className="h-4 w-4" /> New API Key</button>
          {newKey && (
            <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4" data-testid="new-key-banner">
              <p className="text-xs font-bold uppercase tracking-widest text-amber-700">Your new key — store it securely</p>
              <div className="mt-2 flex items-center gap-2">
                <code className="mono flex-1 rounded-lg bg-white px-4 py-2.5 text-sm">{newKey}</code>
                <button onClick={() => copy(newKey)} data-testid="copy-key-btn" className="rounded-lg bg-shipany-navy p-2.5 text-white"><Copy className="h-4 w-4" /></button>
              </div>
              <button onClick={() => setNewKey(null)} className="mt-2 text-xs font-semibold text-amber-700 underline">I've saved it</button>
            </div>
          )}
          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="keys-table">
            <table className="w-full min-w-[640px] text-sm">
              <thead><tr className="bg-shipany-navy text-left text-xs uppercase tracking-wider text-white"><th className="px-5 py-3">Name</th><th className="px-4 py-3">Key</th><th className="px-4 py-3">Mode</th><th className="px-4 py-3">Rate limit</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th></tr></thead>
              <tbody>
                {keys.map((k) => (
                  <tr key={k.key_id} className="border-t border-slate-100" data-testid={`key-row-${k.key_id}`}>
                    <td className="px-5 py-3 font-semibold text-shipany-navy">{k.name}</td>
                    <td className="mono px-4 py-3 text-xs text-slate-500">{k.prefix}</td>
                    <td className="px-4 py-3"><span className={`rounded px-2 py-0.5 text-[10px] font-bold uppercase ${k.mode === "production" ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>{k.mode}</span></td>
                    <td className="mono px-4 py-3 text-xs">{k.rate_limit}/min</td>
                    <td className="px-4 py-3 text-xs font-bold">{k.active ? <span className="text-emerald-600">ACTIVE</span> : <span className="text-slate-400">REVOKED</span>}</td>
                    <td className="px-4 py-3 text-right">{k.active && <button onClick={async () => { await api.delete(`/developer/keys/${k.key_id}`); load(); }} data-testid={`revoke-${k.key_id}`} className="rounded-lg p-1.5 text-red-400 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>}</td>
                  </tr>
                ))}
                {!keys.length && <tr><td colSpan={6} className="px-6 py-10 text-center text-slate-400">No API keys yet.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="rounded-xl bg-slate-50 p-4 text-xs text-slate-500">
            Authenticate with header <code className="mono font-bold">X-API-Key: sk_live_…</code> · Sandbox keys (<code className="mono">sk_test_</code>) tag shipments as [SANDBOX].
          </div>
        </div>
      )}

      {tab === "webhooks" && (
        <div className="mt-5 space-y-4">
          <button onClick={() => setWhOpen(true)} data-testid="create-webhook-btn" className="flex items-center gap-2 rounded-xl bg-shipany-blue px-5 py-2.5 text-sm font-bold text-white hover:bg-[#0648B3] glow-blue"><Plus className="h-4 w-4" /> Add Endpoint</button>
          <div className="space-y-3" data-testid="webhooks-list">
            {webhooks.map((w) => (
              <div key={w.endpoint_id} className="rounded-2xl border border-slate-200 bg-white p-5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="mono text-sm font-semibold text-shipany-navy">{w.url}</p>
                  <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${w.active ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>{w.active ? "ACTIVE" : "DISABLED"}</span>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">{w.events.map((e) => <span key={e} className="rounded bg-shipany-blue/10 px-2 py-0.5 text-[10px] font-bold text-shipany-blue">{e}</span>)}</div>
                <div className="mt-3 flex items-center gap-3 text-xs text-slate-500">
                  <span className="mono">secret: {w.secret.slice(0, 10)}…</span>
                  <button onClick={() => copy(w.secret)} className="text-shipany-blue hover:underline">copy</button>
                  <span>{w.deliveries_total} deliveries · {w.deliveries_dead} dead</span>
                  {w.active && <button onClick={async () => { await api.delete(`/developer/webhooks/${w.endpoint_id}`); load(); }} className="ml-auto text-red-500 hover:underline">Disable</button>}
                </div>
              </div>
            ))}
            {!webhooks.length && <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-400">No webhook endpoints. Events are signed with X-Shipany-Signature (HMAC-SHA256).</div>}
          </div>
          <h3 className="font-display text-sm font-bold text-shipany-navy">Recent deliveries (retries with backoff → DLQ after 5)</h3>
          <div className="space-y-2" data-testid="deliveries-list">
            {deliveries.slice(0, 15).map((d) => (
              <div key={d.delivery_id} className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs">
                <span className={`rounded px-2 py-0.5 font-bold ${d.status === "success" ? "bg-emerald-50 text-emerald-600" : d.status === "dead" ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-600"}`}>{d.status.toUpperCase()}</span>
                <span className="mono font-semibold text-shipany-blue">{d.event}</span>
                <span className="mono max-w-56 truncate text-slate-400">{d.url}</span>
                <span className="text-slate-400">attempts {d.attempts} · {d.response_code || "—"}</span>
                <span className="ml-auto text-slate-400">{fmtDateTime(d.created_at)}</span>
                {d.status !== "success" && <button onClick={async () => { await api.post(`/developer/webhooks/deliveries/${d.delivery_id}/replay`); toast.success("Replayed"); load(); }} data-testid={`replay-${d.delivery_id}`} className="rounded-lg bg-shipany-navy px-3 py-1 font-bold text-white">Replay</button>}
              </div>
            ))}
            {!deliveries.length && <p className="text-xs text-slate-400">No deliveries yet.</p>}
          </div>
        </div>
      )}

      {tab === "usage" && (
        <div className="mt-5 grid gap-5 lg:grid-cols-2" data-testid="usage-panel">
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-400">API calls (7 days)</p>
            <p className="mono mt-2 text-3xl font-bold text-shipany-navy">{usage?.total_7d ?? 0}</p>
            <div className="mt-4 space-y-1.5">
              {Object.entries(usage?.by_endpoint || {}).map(([ep, n]) => (
                <div key={ep} className="flex justify-between text-xs"><span className="mono text-slate-500">{ep}</span><span className="mono font-bold">{n}</span></div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Recent calls</p>
            <div className="mt-3 max-h-64 space-y-1.5 overflow-y-auto">
              {(usage?.recent || []).map((l) => (
                <p key={l.usage_id} className="mono text-[11px] text-slate-500">{l.at?.slice(0, 19).replace("T", " ")} · {l.endpoint} · {l.mode}</p>
              ))}
              {!(usage?.recent || []).length && <p className="text-xs text-slate-400">No API usage yet.</p>}
            </div>
          </div>
        </div>
      )}

      {tab === "docs" && (
        <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-6" data-testid="api-docs">
          <p className="text-sm text-slate-600">Base URL: <code className="mono font-bold text-shipany-blue">{API_BASE}</code> · Auth: <code className="mono font-bold text-shipany-blue">X-API-Key</code> header.</p>
          <div className="mt-4 space-y-2.5">
            {DOCS.map(([m, p, d]) => (
              <div key={p} className="flex flex-wrap items-center gap-3 rounded-xl bg-slate-50 px-4 py-3">
                <span className={`mono rounded px-2 py-0.5 text-xs font-bold ${m === "GET" ? "bg-emerald-50 text-emerald-600" : "bg-shipany-blue/10 text-shipany-blue"}`}>{m}</span>
                <code className="mono text-sm font-semibold text-shipany-navy">{p}</code>
                <span className="text-xs text-slate-500">{d}</span>
              </div>
            ))}
          </div>
          <pre className="mono mt-5 overflow-x-auto rounded-xl bg-shipany-navy p-4 text-xs text-slate-300">{`curl -X POST ${API_BASE}/api/v1/rates \\
  -H "X-API-Key: sk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{"pickup_pincode":"122001","delivery_pincode":"400001","weight":1}'`}</pre>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="key-modal">
          <div className="absolute inset-0 bg-shipany-navy/60 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-sm rounded-2xl bg-white p-6">
            <div className="flex items-center justify-between"><h2 className="font-display text-lg font-bold text-shipany-navy">New API Key</h2><button onClick={() => setOpen(false)} className="text-slate-400"><X className="h-5 w-5" /></button></div>
            <div className="mt-4 space-y-3.5">
              <input data-testid="key-name" className={inputCls} placeholder="Key name (e.g. Production ERP)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <select data-testid="key-mode" className={inputCls} value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })}>
                <option value="sandbox">Sandbox (testing)</option><option value="production">Production (live)</option>
              </select>
            </div>
            <button onClick={createKey} data-testid="key-create-confirm" className="mt-5 w-full rounded-xl bg-shipany-blue py-3 text-sm font-bold text-white hover:bg-[#0648B3]">Generate Key</button>
          </div>
        </div>
      )}

      {whOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="webhook-modal">
          <div className="absolute inset-0 bg-shipany-navy/60 backdrop-blur-sm" onClick={() => setWhOpen(false)} />
          <div className="relative w-full max-w-md rounded-2xl bg-white p-6">
            <div className="flex items-center justify-between"><h2 className="font-display text-lg font-bold text-shipany-navy">Webhook Endpoint</h2><button onClick={() => setWhOpen(false)} className="text-slate-400"><X className="h-5 w-5" /></button></div>
            <div className="mt-4 space-y-3.5">
              <input data-testid="webhook-url" className={`${inputCls} mono`} placeholder="https://yourapp.com/webhooks/shipany" value={whForm.url} onChange={(e) => setWhForm({ ...whForm, url: e.target.value })} />
              <p className="text-xs font-semibold text-slate-500">Events</p>
              <div className="flex flex-wrap gap-1.5">
                {EVENTS.map((e) => (
                  <button key={e} type="button" onClick={() => setWhForm({ ...whForm, events: whForm.events.includes(e) ? whForm.events.filter((x) => x !== e) : [...whForm.events, e] })}
                    data-testid={`webhook-event-${e.replace(/\./g, "-")}`}
                    className={`rounded-lg px-2.5 py-1 text-[11px] font-bold ${whForm.events.includes(e) ? "bg-shipany-blue text-white" : "bg-slate-100 text-slate-500"}`}>{e}</button>
                ))}
              </div>
            </div>
            <button onClick={createWebhook} data-testid="webhook-create-confirm" className="mt-5 w-full rounded-xl bg-shipany-blue py-3 text-sm font-bold text-white hover:bg-[#0648B3]">Register Endpoint</button>
          </div>
        </div>
      )}
    </div>
  );
}
